import useHasAccess from "@hooks/useHasAccess"
import useIsDesktop from "@hooks/useIsDesktop"
import useSlugExtractor from "@hooks/useSlugExtractor"

import { AccessCheck } from "@particles"

import { NavTopBar, ObjectsView, ViewBar, ViewMenu } from "@organisms"
import CardsView from "@organisms/ObjectsView/CardsView/CardsView"
import TableView from "@organisms/ObjectsView/TableView/TableView"
import ViewTypesProvider from "@organisms/ObjectsView/ViewTypesProvider/ViewTypesProvider"

import { AppFrame } from "@templates"

import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"

import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import {
    CREDIT_NOTES_LIST_CONTROLS_ACTIVE_TAB,
    CREDIT_NOTES_LIST_CONTROLS_KEYWORD_SEARCH,
    CREDIT_NOTES_LIST_CONTROLS_PHASE,
    CREDIT_NOTES_LIST_CONTROLS_VIEW,
} from "@constants/storage"

import {
    CREDIT_NOTE_CARDS_VIEW_ATTRIBUTE_OPTIONS,
    CREDIT_NOTE_CARDS_VIEW_SORT_OPTIONS,
} from "./CreditNoteList.constants"
import { CreditNoteObjectsViewTabs } from "./CreditNoteObjectsViewTabs"
import CreditNoteCardsView from "./views/CreditNoteCardsView/CreditNoteCardsView"
import CreditNoteTableView from "./views/CreditNoteTableView/CreditNoteTableView"

export default function CreditNoteList() {
    const isDesktop = useIsDesktop()
    const { hasPermission } = useHasAccess()
    const [createRoute] = useSlugExtractor([CREDIT_NOTES_ROUTES.CREATE])

    return (
        <AppFrame.Root collaborationRoomName="credit-note-list">
            <ViewTypesProvider
                viewTypes={["table", "cards"]}
                viewTypeKey={CREDIT_NOTES_LIST_CONTROLS_VIEW}
                defaultViewType={isDesktop ? "table" : "cards"}
            >
                {({ activeViewType }) => (
                    <>
                        <NavTopBar.Root>
                            <NavTopBar.Header>
                                <NavTopBar.NavTopBarHeaderStart>
                                    <NavTopBar.PageTitle
                                        label={objectMap.credit_note.labelPlural}
                                        icon={objectMap.credit_note.iconActive}
                                    />
                                </NavTopBar.NavTopBarHeaderStart>
                                <NavTopBar.NavTopBarHeaderEnd>
                                    <NavTopBar.PagePresence />
                                    <AccessCheck
                                        permissions={{
                                            invoices_list_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <AccessCheck
                                            permissions={{
                                                invoices_view_permission: PERMISSION_LEVEL.FULL,
                                            }}
                                        >
                                            <NavTopBar.SearchButton scope="Credit notes" />
                                        </AccessCheck>
                                    </AccessCheck>
                                    <AccessCheck
                                        permissions={{
                                            invoices_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                        }}
                                    >
                                        <NavTopBar.CTAButton destination={createRoute} />
                                    </AccessCheck>
                                </NavTopBar.NavTopBarHeaderEnd>
                            </NavTopBar.Header>
                        </NavTopBar.Root>
                        <ObjectsView.Provider
                            activePhaseStorageKey={CREDIT_NOTES_LIST_CONTROLS_PHASE}
                            activeTabStorageKey={CREDIT_NOTES_LIST_CONTROLS_ACTIVE_TAB}
                            searchKeywordsStorageKey={CREDIT_NOTES_LIST_CONTROLS_KEYWORD_SEARCH}
                            listTitle="Credit Notes"
                            objectType="credit_note"
                            objectIndexName="CreditNote"
                            objectName="Credit Note"
                            defaultActivePhase="open"
                            objectCreateRoute={createRoute}
                            isRowClickable={hasPermission("invoices_view_permission", PERMISSION_LEVEL.FULL)}
                            isEmptyStateCreateButtonVisible={hasPermission(
                                "invoices_create_permission",
                                PERMISSION_LEVEL.RESTRICTED,
                            )}
                        >
                            <TableView.Provider>
                                <CardsView.Provider
                                    attributes={CREDIT_NOTE_CARDS_VIEW_ATTRIBUTE_OPTIONS}
                                    sortOptions={CREDIT_NOTE_CARDS_VIEW_SORT_OPTIONS}
                                >
                                    <ViewBar.Root>
                                        <ViewBar.Start>
                                            <ViewBar.Phases>
                                                <ObjectsView.Phase phase="open" title="Open" />
                                                <ObjectsView.Phase phase="closed" title="Closed" />
                                            </ViewBar.Phases>
                                            <ViewBar.VerticalDivider />
                                            <CreditNoteObjectsViewTabs />
                                        </ViewBar.Start>
                                        <ViewBar.End>
                                            <ObjectsView.SearchInput />
                                            <ViewMenu />
                                        </ViewBar.End>
                                    </ViewBar.Root>
                                    {activeViewType === "table" && <CreditNoteTableView />}
                                    {activeViewType === "cards" && <CreditNoteCardsView />}
                                </CardsView.Provider>
                            </TableView.Provider>
                        </ObjectsView.Provider>
                    </>
                )}
            </ViewTypesProvider>
        </AppFrame.Root>
    )
}
